import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import type { AccountItem, PaymentStatus, AccountingFilter, AccountingStats } from "@/types/accounting";
import { useUser } from "@/context/UserContext";
import { renderOrder, type WineOrder } from "@/pages/haa/document";
import { getToken } from "@/lib/authToken";
import { SALES_SPECIALISTS } from "@/lib/salesSpecialists";

const paymentStatusTabs: Array<{ value: PaymentStatus | "all"; label: string }> = [
  { value: "all", label: "全部" },
  { value: "未付款", label: "待處理" },
  { value: "已付款", label: "已完成" },
];

const PAGE_SIZE = 10;

const ASSIGNEES = ["未指派", ...SALES_SPECIALISTS];

const ASSIGNEE_QUOTAS: Record<string, number> = {
  "陳宇澤": 400000,
  "林沐晴": 400000,
  "王子睿": 400000,
  "李若彤": 400000,
  "張皓軒": 300000,
  "劉語芯": 300000,
};

interface EmployeeOption {
  id: string;
  name: string;
  email: string;
}

function getPaymentStatusColor(status: PaymentStatus): string {
  switch (status) {
    case "未付款": return "bg-accent-100 text-accent-700";
    case "已通知": return "bg-secondary-100 text-secondary-700";
    case "已付款": return "bg-primary-100 text-primary-700";
    default: return "bg-background-100 text-foreground-600";
  }
}

function formatDisplayDate(value: string): string {
  if (!value) return "—";
  return value.includes("T") ? value.split("T")[0] : value;
}

function mapRowToItem(row: Record<string, unknown>): AccountItem {
  return {
    id: row.id as string,
    orderNumber: row.order_number as string,
    company: row.company as string,
    product: row.product as string,
    quantity: (row.quantity as number) || 1,
    unit: (row.unit as string) || "瓶",
    amount: Number(row.amount) || 0,
    currency: (row.currency as string) || "NT$",
    dueDate: row.due_date as string,
    transferTime: row.transfer_time as string,
    paymentStatus: row.payment_status as PaymentStatus,
    assignee: (row.assignee as string) || "未指派",
    status: (row.status as string) || "處理中",
    note: (row.note as string) || "",
    receiptUrl: (row.receipt_url as string) || null,
    employeeId: row.employee_id as string,
    createdAt: row.created_at as string,
    updatedAt: (row.updated_at as string) || (row.created_at as string),
  };
}

export default function AccountingPage() {
  const user = useUser();
  const [filter, setFilter] = useState<AccountingFilter>({
    search: "",
    paymentStatus: "all",
    assignee: "all",
    employeeId: user.isAdmin ? "all" : (user.employeeId || ""),
  });
  const [items, setItems] = useState<AccountItem[]>([]);
  const [employeeNameSearch, setEmployeeNameSearch] = useState("");
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [submittingTrial, setSubmittingTrial] = useState(false);
  const [generatingCount, setGeneratingCount] = useState<number | null>(null);
  const [receiptTargetId, setReceiptTargetId] = useState<string | null>(null);
  const [previewReceiptUrl, setPreviewReceiptUrl] = useState<string | null>(null);
  const receiptInputRef = useRef<HTMLInputElement>(null);
  const hasAutoGeneratedRef = useRef(false);

  const showToast = useCallback((message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Auto-generate orders if fewer than 20 records exist
  useEffect(() => {
    if (!user.isAdmin) return;
    if (hasAutoGeneratedRef.current) return;
    if (loading) return;
    if (items.length >= 20) {
      hasAutoGeneratedRef.current = true;
      return;
    }

    const generateMissing = async () => {
      hasAutoGeneratedRef.current = true;

      // Determine which employee_id to generate orders for
      let targetEmployeeId = filter.employeeId;
      if (targetEmployeeId === "all" || !targetEmployeeId) {
        // Admin viewing all: use own employee_id if available, otherwise first employee
        targetEmployeeId = user.employeeId || employees[0]?.id || "";
      }
      if (!targetEmployeeId) {
        showToast("無法自動生成：找不到對應的員工 ID", "error");
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("generate-accounting-orders", {
          body: { employee_id: targetEmployeeId },
        });

        if (error) {
          showToast(`自動生成失敗：${error.message}`, "error");
          return;
        }

        if (data?.success && data.inserted > 0) {
          showToast(`已自動生成 ${data.inserted} 筆帳務訂單`);
        }

        // Re-fetch to include newly inserted orders
        const { data: freshData } = await supabase
          .from("accounting_items")
          .select("*")
          .order("created_at", { ascending: false });
        if (freshData) {
          setItems((freshData as unknown[]).map((row) => mapRowToItem(row as Record<string, unknown>)));
        }
      } catch (err) {
        showToast(`自動生成失敗：${(err as Error).message}`, "error");
      }
    };

    generateMissing();
  }, [items.length, loading, filter.employeeId, user.employeeId, user.isAdmin, employees, showToast]);

  // Fetch employees list
  useEffect(() => {
    supabase
      .from("employees")
      .select("id, name, email")
      .order("name")
      .then(({ data, error }) => {
        if (!error && data) {
          setEmployees(data as EmployeeOption[]);
        }
      });
  }, []);

  // Fetch accounting items
  const fetchItems = useCallback(() => {
    setLoading(true);
    setLoadError("");
    supabase
      .from("accounting_items")
      .select("*")
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (error) {
          setLoadError(error.message);
          setItems([]);
        } else if (data) {
          setItems((data as unknown[]).map((row) => mapRowToItem(row as Record<string, unknown>)));
        }
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const availableEmployees = useMemo(() => {
    if (user.isAdmin) return employees;
    return employees.filter((e) => e.id === user.employeeId);
  }, [user.isAdmin, user.employeeId, employees]);

  const nameSearchItems = useMemo(() => {
    const keyword = employeeNameSearch.trim().toLocaleLowerCase();
    const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Taipei", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
    const todayItems = items.filter((item) => {
      const source = item.createdAt || item.transferTime;
      if (!source) return false;
      return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Taipei", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(source)) === today;
    });
    if (!user.isAdmin || !keyword) return todayItems;
    const matchedIds = new Set(employees.filter((emp) => emp.name.toLocaleLowerCase().includes(keyword)).map((emp) => emp.id));
    return todayItems.filter((item) => matchedIds.has(item.employeeId));
  }, [items, employees, employeeNameSearch, user.isAdmin]);
  // Today's date string for daily quota tracking — MUST be declared before filteredItems which uses it
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Today assigned items list for the cards above quota overview
  const todayAssignedItems = useMemo(() => {
    const scopeItems = filter.employeeId === "all" ? nameSearchItems : nameSearchItems.filter((i) => i.employeeId === filter.employeeId);
    return scopeItems.filter((i) => i.assignee !== "未指派" && i.paymentStatus !== "已付款");
  }, [nameSearchItems, filter.employeeId]);

  const filteredItems = useMemo(() => {
    let result = [...nameSearchItems];
    if (filter.employeeId !== "all") {
      result = result.filter((i) => i.employeeId === filter.employeeId);
    }
    if (filter.paymentStatus !== "all") {
      result = result.filter((i) => filter.paymentStatus === "未付款"
        ? i.paymentStatus !== "已付款"
        : i.paymentStatus === "已付款");
    }
    if (filter.assignee !== "all") {
      result = result.filter((i) => i.assignee === filter.assignee);
    }
    if (filter.search.trim()) {
      const term = filter.search.toLowerCase();
      result = result.filter(
        (i) =>
          i.orderNumber.toLowerCase().includes(term) ||
          i.company.toLowerCase().includes(term) ||
          i.product.toLowerCase().includes(term)
      );
    }
    // Sort: 已付款 at the bottom, rest by created_at desc
    result.sort((a, b) => {
      if (a.paymentStatus === "已付款" && b.paymentStatus !== "已付款") return 1;
      if (a.paymentStatus !== "已付款" && b.paymentStatus === "已付款") return -1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    return result;
  }, [nameSearchItems, filter]);

  const stats: AccountingStats = useMemo(() => {
    const relevant = filter.employeeId === "all" ? nameSearchItems : nameSearchItems.filter((i) => i.employeeId === filter.employeeId);
    const unpaid = relevant.filter((i) => i.paymentStatus === "未付款");
    const notified = relevant.filter((i) => i.paymentStatus === "已通知");
    const paid = relevant.filter((i) => i.paymentStatus === "已付款");
    const totalAmount = relevant.reduce((sum, i) => sum + i.amount, 0);
    const paidAmount = paid.reduce((sum, i) => sum + i.amount, 0);
    const collectionRate = totalAmount > 0 ? Math.round((paidAmount / totalAmount) * 100) : 0;
    return {
      totalAmount,
      totalCount: relevant.length,
      unpaidAmount: unpaid.reduce((sum, i) => sum + i.amount, 0),
      unpaidCount: unpaid.length,
      notifiedAmount: notified.reduce((sum, i) => sum + i.amount, 0),
      notifiedCount: notified.length,
      paidAmount,
      paidCount: paid.length,
      collectionRate,
    };
  }, [nameSearchItems, filter.employeeId]);

  // Trial calculation: per-assignee usage from today's assigned items (all payment statuses)
  const trialAssigneeData = useMemo(() => {
    // Only consider items matching the current employee filter
    const scopeItems = filter.employeeId === "all" ? nameSearchItems : nameSearchItems.filter((i) => i.employeeId === filter.employeeId);
    // Use updatedAt to track "today": assignee changes & payment status changes both update updated_at,
    // so today's edits are counted in today's quota. Previous-day items auto-clear next day.
    const activeItems = scopeItems.filter((i) => i.assignee !== "未指派" && i.updatedAt.slice(0, 10) === todayStr);

    return ASSIGNEES.filter((name) => name !== "未指派").map((name) => {
      const assigned = activeItems.filter((i) => i.assignee === name);
      const usedAmount = assigned.reduce((sum, i) => sum + i.amount, 0);
      const quota = ASSIGNEE_QUOTAS[name] || 0;
      const remaining = Math.max(0, quota - usedAmount);
      const usagePct = quota > 0 ? Math.min(100, Math.round((usedAmount / quota) * 100)) : 0;
      return {
        name,
        quota,
        usedAmount,
        remaining,
        usagePct,
        assignedCount: assigned.length,
      };
    });
  }, [nameSearchItems, filter.employeeId, todayStr]);

  // Trial items that will be submitted (currently assigned, not paid)
  const trialPendingItems = useMemo(() => {
    const scopeItems = filter.employeeId === "all" ? nameSearchItems : nameSearchItems.filter((i) => i.employeeId === filter.employeeId);
    return scopeItems.filter((i) => i.paymentStatus !== "已付款" && i.assignee !== "未指派");
  }, [nameSearchItems, filter.employeeId]);

  const trialTotalAmount = useMemo(() => {
    return trialPendingItems.reduce((sum, i) => sum + i.amount, 0);
  }, [trialPendingItems]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const paginatedItems = filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter.paymentStatus, filter.search, filter.employeeId, filter.assignee, employeeNameSearch]);

  const handlePaymentStatusChange = useCallback(async (itemId: string, newStatus: PaymentStatus) => {
    const newOverallStatus = newStatus === "已付款" ? "已結案" : "處理中";
    const nowStr = new Date().toISOString();
    setItems((prev) =>
      prev.map((i) =>
        i.id === itemId
          ? { ...i, paymentStatus: newStatus, status: newOverallStatus, updatedAt: nowStr }
          : i
      )
    );

    const { error } = await supabase
      .from("accounting_items")
      .update({
        payment_status: newStatus,
        status: newOverallStatus,
        updated_at: new Date().toISOString(),
      })
      .eq("id", itemId);

    if (error) {
      showToast(`更新失敗：${error.message}`, "error");
      fetchItems();
    } else {
      showToast(`付款狀態已更新為「${newStatus}」`);
    }
  }, [showToast, fetchItems]);

  const handleAssigneeChange = useCallback(async (itemId: string, newAssignee: string) => {
    const nowStr = new Date().toISOString();
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, assignee: newAssignee, updatedAt: nowStr } : i)));

    const { error } = await supabase
      .from("accounting_items")
      .update({ assignee: newAssignee, updated_at: new Date().toISOString() })
      .eq("id", itemId);

    if (error) {
      showToast(`更新失敗：${error.message}`, "error");
      fetchItems();
    }
  }, [showToast, fetchItems]);

  const handleDeleteItem = useCallback(async (itemId: string) => {
    if (!user.isAdmin) {
      showToast("只有管理員可以刪除訂單", "error");
      return;
    }
    if (!window.confirm("確定刪除這筆帳務訂單？刪除後無法復原。")) return;
    const { data, error } = await supabase.functions.invoke("delete-accounting-order", { body: { item_id: itemId } });
    if (error || !data?.success) {
      showToast(`刪除失敗：${error?.message || data?.error || "未知錯誤"}`, "error");
      return;
    }

    setItems((prev) => prev.filter((i) => i.id !== itemId));
    showToast("帳務記錄已刪除");
  }, [user.isAdmin, showToast]);

  // Submit trial: bulk mark all assigned non-paid items as "已付款"
  const handleTrialSubmit = useCallback(async () => {
    if (trialPendingItems.length === 0) {
      showToast("目前沒有可送出的訂單（需先指派專員且尚未付款）", "error");
      return;
    }

    setSubmittingTrial(true);
    const ids = trialPendingItems.map((i) => i.id);

    const { error } = await supabase
      .from("accounting_items")
      .update({
        payment_status: "已付款",
        status: "已結案",
        updated_at: new Date().toISOString(),
      })
      .in("id", ids);

    setSubmittingTrial(false);

    if (error) {
      showToast(`送出失敗：${error.message}`, "error");
      return;
    }

    // Update local state
    const nowStr = new Date().toISOString();
    setItems((prev) =>
      prev.map((i) =>
        ids.includes(i.id)
          ? { ...i, paymentStatus: "已付款" as PaymentStatus, status: "已結案", updatedAt: nowStr }
          : i
      )
    );

    const totalAmount = trialPendingItems.reduce((sum, i) => sum + i.amount, 0);
    showToast(`送出成功！${trialPendingItems.length} 筆訂單已設為已付款，總額 ${formatCurrency(totalAmount)}`);
  }, [trialPendingItems, showToast]);

  const formatCurrency = (amount: number) => `NT$ ${amount.toLocaleString("zh-TW")}`;
  const selectedEmployeeName = filter.employeeId === "all"
    ? "全部員工"
    : employees.find((e) => e.id === filter.employeeId)?.name || "";

  const downloadPaymentList = useCallback((item: AccountItem) => {
    const todayTaipei = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Taipei", year: "numeric", month: "2-digit", day: "2-digit",
    }).format(new Date());
    const originalQuantity = Math.max(1, Number(item.quantity) || 1);
    const originalUnitPrice = Math.max(1, Number(item.amount) / originalQuantity);
    const targetTotal = Math.min(100000, Math.max(50000, Number(item.amount) || 50000));
    let quantity = originalQuantity;
    let price = Math.max(1, Math.floor(targetTotal / quantity));
    if (price * quantity < 50000) {
      quantity = Math.max(quantity, Math.ceil(50000 / originalUnitPrice));
      price = Math.max(1, Math.floor(targetTotal / quantity));
    }
    const order: WineOrder = {
      date: todayTaipei, number: item.orderNumber,
      author: user.userName || "帳務人員", customer: item.company, contact: "—",
      salesperson: item.assignee, sku: item.orderNumber, status: item.status || "待處理",
      product: item.product, origin: "—", vintage: "—", volume: item.unit || "瓶", alcohol: "—",
      quantity: String(quantity), price: String(price),
      deposit: "0", payment: "待確認", description: "",
      notes: item.note || "請核對訂單、數量與收款金額。",
    };
    const url = URL.createObjectURL(new Blob([renderOrder(order)], { type: "text/html;charset=utf-8" }));
    const link = document.createElement("a"); link.href = url;
    link.download = `${item.orderNumber.replace(/[^\w\u4e00-\u9fff-]/g, "_")}-支付清單.html`;
    link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [user.userName]);

  const handleReceiptUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const itemId = receiptTargetId;
    event.target.value = "";
    if (!file || !itemId) return;
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      showToast("收款證明僅支援圖片或 PDF", "error"); return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast("檔案不可超過 10MB", "error"); return;
    }
    const body = new FormData(); body.append("file", file); body.append("item_id", itemId);
    const response = await fetch(`${import.meta.env.VITE_API_URL || ""}/api/functions/upload-accounting-receipt`, {
      method: "POST", headers: { Authorization: `Bearer ${getToken() || ""}` }, body,
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.success) { showToast(result.error || "上傳失敗", "error"); return; }
    setItems((prev) => prev.map((item) => item.id === itemId ? { ...item, receiptUrl: result.url } : item));
    showToast("收款證明已上傳");
  };

  const generateForEmployee = useCallback(async (count: number) => {
    if (!user.isAdmin || !filter.employeeId || filter.employeeId === "all") {
      showToast("請先選擇指定員工", "error"); return;
    }
    setGeneratingCount(count);
    const { data, error } = await supabase.functions.invoke("generate-accounting-orders", {
      body: { employee_id: filter.employeeId, count },
    });
    setGeneratingCount(null);
    if (error) { showToast(`生成失敗：${error.message}`, "error"); return; }
    await fetchItems();
    showToast(`已為 ${selectedEmployeeName || "指定員工"} 生成 ${data?.inserted ?? count} 筆帳務訂單`);
  }, [user.isAdmin, filter.employeeId, selectedEmployeeName, fetchItems, showToast]);

  return (
    <div className="space-y-5 max-w-[1440px] mx-auto">
      <input ref={receiptInputRef} type="file" accept="image/*,.pdf" className="hidden" onChange={handleReceiptUpload} />
      {previewReceiptUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="收款證明預覽" onClick={() => setPreviewReceiptUrl(null)}>
          <div className="relative max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-xl bg-background-50 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
              <h2 className="text-sm font-semibold text-foreground-900">收款證明</h2>
              <button type="button" onClick={() => setPreviewReceiptUrl(null)} className="flex h-8 w-8 items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100" aria-label="關閉預覽"><i className="ri-close-line" /></button>
            </div>
            <div className="max-h-[calc(90vh-57px)] overflow-auto bg-background-100 p-4 text-center">
              {/\.pdf(?:$|\?)/i.test(previewReceiptUrl) ? (
                <iframe src={previewReceiptUrl} title="收款證明 PDF" className="h-[75vh] w-full rounded-lg bg-white" />
              ) : (
                <img src={previewReceiptUrl} alt="收款證明" className="mx-auto max-h-[75vh] max-w-full rounded-lg object-contain" />
              )}
            </div>
          </div>
        </div>
      )}
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
          toast.type === "error" ? "bg-accent-500 text-background-50" : "bg-foreground-800 text-background-50"
        }`}>
          <i className={`${toast.type === "error" ? "ri-error-warning-line" : "ri-check-line"} text-primary-400`} />
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>
              帳務管理
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              應收帳款管理{user.isAdmin ? `，資料依員工隔離（${user.userEmail}）` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { fetchItems(); showToast("資料已刷新"); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap shrink-0"
            >
              <i className="ri-refresh-line" />
              全部刷新
            </button>
          </div>
        </div>

        {/* Employee selector */}
        {user.isAdmin && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-background-200 bg-background-50 w-full sm:w-auto">
            <i className="ri-search-line text-foreground-400 text-sm" />
            <input type="search" aria-label="員工名稱搜尋" placeholder="搜尋員工名稱"
              value={employeeNameSearch} onChange={(e) => setEmployeeNameSearch(e.target.value)}
              className="w-full sm:w-[180px] text-sm text-foreground-900 bg-transparent outline-none" />
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-background-200 bg-background-50 w-full sm:w-auto min-w-0">
            <i className="ri-user-3-line text-foreground-400 text-sm" />
            <select
              value={filter.employeeId}
              onChange={(e) => setFilter((prev) => ({ ...prev, employeeId: e.target.value }))}
              className="flex-1 sm:flex-none min-w-0 sm:min-w-[160px] text-sm text-foreground-900 bg-transparent border-none outline-none cursor-pointer"
            >
              {user.isAdmin && <option value="all">全部員工</option>}
              {availableEmployees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}（{emp.email}）
                </option>
              ))}
            </select>
          </div>
          <span className="text-xs text-foreground-400">
            {selectedEmployeeName ? `目前檢視：${selectedEmployeeName}` : ""}
          </span>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-foreground-500">生成指定員工</span>
            {[1, 2, 3].map((count) => (
              <button key={count} type="button" onClick={() => generateForEmployee(count)} disabled={generatingCount !== null}
                className="rounded-lg bg-primary-500 px-3 py-2 text-xs font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed">
                {generatingCount === count ? <i className="ri-loader-4-line animate-spin" /> : `${count} 筆`}
              </button>
            ))}
          </div>
          {loading && (
            <span className="text-xs text-foreground-400 flex items-center gap-1">
              <i className="ri-loader-4-line animate-spin" />
              載入中...
            </span>
          )}
          {loadError && (
            <span className="text-xs text-accent-600 flex items-center gap-1">
              <i className="ri-error-warning-line" />
              {loadError}
              <button onClick={fetchItems} className="underline cursor-pointer ml-1">重試</button>
            </span>
          )}
        </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-background-200 bg-background-50 p-5"><p className="text-sm text-foreground-500">待處理</p><p className="mt-2 text-3xl font-semibold text-foreground-900">{stats.unpaidCount + stats.notifiedCount} 筆</p><p className="mt-1 text-sm text-foreground-500">{formatCurrency(stats.unpaidAmount + stats.notifiedAmount)}</p></div>
        <div className="rounded-xl border border-background-200 bg-background-50 p-5"><p className="text-sm text-foreground-500">已完成</p><p className="mt-2 text-3xl font-semibold text-foreground-900">{stats.paidCount} 筆</p><p className="mt-1 text-sm text-foreground-500">{formatCurrency(stats.paidAmount)}</p></div>
      </div>
      {/* Order list */}
      <div className="bg-background-50 border border-background-200 rounded-xl">
        <div className="p-4 border-b border-background-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-sm font-bold text-foreground-900 flex items-center gap-2">
                <i className="ri-file-list-3-line text-primary-500" />
                訂單尾款列表
              </h2>
              <p className="text-xs text-foreground-500 mt-0.5">
                訂單尾款與收款確認；每筆訂單可單獨指定員工
              </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
              <select
                value={filter.assignee}
                onChange={(e) => setFilter((prev) => ({ ...prev, assignee: e.target.value }))}
                className="w-full sm:w-auto text-xs px-3 py-2.5 sm:py-2 rounded-lg border border-background-200 bg-background-50 outline-none cursor-pointer"
              >
                <option value="all">全部專員</option>
                {ASSIGNEES.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <div className="relative w-full sm:w-48">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
                <input
                  type="text"
                  value={filter.search}
                  onChange={(e) => setFilter((prev) => ({ ...prev, search: e.target.value }))}
                  placeholder="搜尋 訂單 / 公司 / 商品"
                  className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                />
              </div>
            </div>
          </div>

          {/* Payment Status Tabs */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {paymentStatusTabs.map((tab) => {
              const count = tab.value === "all"
                ? filteredItems.length
                : nameSearchItems.filter((i) => {
                    if (filter.employeeId !== "all" && i.employeeId !== filter.employeeId) return false;
                    return tab.value === "未付款" ? i.paymentStatus !== "已付款" : i.paymentStatus === tab.value;
                  }).length;
              return (
                <button
                  key={tab.value}
                  onClick={() => setFilter((prev) => ({ ...prev, paymentStatus: tab.value }))}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                    filter.paymentStatus === tab.value
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {tab.label}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    filter.paymentStatus === tab.value ? "bg-background-50/20 text-background-50" : "bg-background-200 text-foreground-500"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-background-200">
          {paginatedItems.map((item) => (
            <article key={item.id} className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-mono text-foreground-500 break-all">{item.orderNumber}</p>
                  <h3 className="text-base font-semibold text-foreground-900 mt-1 break-words">{item.company}</h3>
                </div>
                <span className={`inline-block text-xs px-2.5 py-1 rounded-lg shrink-0 ${item.status === "已結案" ? "bg-primary-100 text-primary-700" : "bg-secondary-100 text-secondary-700"}`}>
                  {item.status}
                </span>
              </div>

              <div className="rounded-lg bg-background-100/70 p-3">
                <p className="text-sm text-foreground-800 break-words">{item.product}</p>
                <div className="flex items-center justify-between gap-3 mt-2">
                  <span className="text-xs text-foreground-500">{item.quantity}{item.unit}</span>
                  <span className="text-sm font-bold text-foreground-900 whitespace-nowrap">{formatCurrency(item.amount)}</span>
                </div>
              </div>

              <dl className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <dt className="text-foreground-400">到期日</dt>
                  <dd className="text-foreground-700 mt-1">{formatDisplayDate(item.dueDate)}</dd>
                </div>
                <div>
                  <dt className="text-foreground-400">轉入時間</dt>
                  <dd className="text-foreground-700 mt-1">{formatDisplayDate(item.transferTime)}</dd>
                </div>
              </dl>

              <div className="grid grid-cols-2 gap-2">
                <label className="space-y-1">
                  <span className="text-[11px] text-foreground-400">付款狀態</span>
                  <select
                    value={item.paymentStatus}
                    onChange={(e) => handlePaymentStatusChange(item.id, e.target.value as PaymentStatus)}
                    className={`w-full min-h-10 text-xs font-medium px-2.5 py-2 rounded-lg border-none outline-none cursor-pointer ${getPaymentStatusColor(item.paymentStatus)}`}
                  >
                    <option value="未付款">未付款</option>
                    <option value="已通知">已通知</option>
                    <option value="已付款">已付款</option>
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-[11px] text-foreground-400">負責專員</span>
                  <select
                    value={item.assignee}
                    onChange={(e) => handleAssigneeChange(item.id, e.target.value)}
                    className="w-full min-h-10 text-xs px-2.5 py-2 rounded-lg border border-background-200 bg-background-50 outline-none cursor-pointer"
                  >
                    <option value="未指派">未指派</option>
                    {ASSIGNEES.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </label>
              </div>

              <button
                onClick={() => downloadPaymentList(item)}
                className="w-full min-h-10 rounded-lg text-sm border border-background-200 text-foreground-700 hover:bg-background-100 flex items-center justify-center gap-1.5"
              >
                <i className="ri-download-2-line" />
                下載支付清單
              </button>
              {user.isAdmin && <button
                onClick={() => handleDeleteItem(item.id)}
                className="w-full min-h-10 rounded-lg text-sm bg-accent-50 text-accent-600 hover:bg-accent-100 flex items-center justify-center gap-1.5"
              >
                <i className="ri-delete-bin-line" />
                刪除帳務記錄
              </button>}
            </article>
          ))}
          {paginatedItems.length === 0 && !loading && (
            <div className="px-4 py-12 text-center text-foreground-400">
              <i className="ri-inbox-line text-2xl mb-2 block" />
              找不到符合條件的帳務紀錄
            </div>
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full table-fixed text-sm [&_td]:px-3 [&_th]:px-3">
            <thead>
              <tr className="border-b border-background-200 bg-background-50">
                <th className="w-[13%] px-3 py-3 text-left text-xs font-medium text-foreground-500">訂單編號</th>
                <th className="w-[35%] px-3 py-3 text-left text-xs font-medium text-foreground-500">公司／商品</th>
                <th className="w-[14%] px-3 py-3 text-right text-xs font-medium text-foreground-500">尾款金額</th>
                <th className="w-[10%] px-3 py-3 text-left text-xs font-medium text-foreground-500">支付清單</th>
                <th className="w-[12%] px-3 py-3 text-left text-xs font-medium text-foreground-500">負責聯絡人</th>
                <th className="w-[10%] px-3 py-3 text-left text-xs font-medium text-foreground-500">收款證明</th>
                <th className="w-[10%] px-3 py-3 text-left text-xs font-medium text-foreground-500">處理狀態</th>
              </tr>
            </thead>
            <tbody>
              {paginatedItems.map((item) => (
                <tr key={item.id} className="border-b border-background-100 hover:bg-background-50/60 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-foreground-700">{item.orderNumber}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-foreground-900">{item.company}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{item.product} × {item.quantity}{item.unit}</p>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium text-foreground-900 whitespace-nowrap">
                    {formatCurrency(item.amount)}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => downloadPaymentList(item)} className="inline-flex items-center gap-1 text-xs text-foreground-700 hover:text-primary-600 cursor-pointer"><i className="ri-download-2-line" />下載</button>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={item.assignee}
                      onChange={(e) => handleAssigneeChange(item.id, e.target.value)}
                      className="text-xs px-2 py-1 rounded border border-background-200 bg-background-50 outline-none cursor-pointer"
                    >
                      <option value="未指派">未指派</option>
                      {ASSIGNEES.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    {item.receiptUrl ? (
                      <button type="button" onClick={() => setPreviewReceiptUrl(item.receiptUrl || null)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-primary-200 text-xs text-primary-700 hover:bg-primary-50"><i className="ri-eye-line" />檢視</button>
                    ) : (
                      <button onClick={() => { setReceiptTargetId(item.id); receiptInputRef.current?.click(); }} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-background-200 text-xs text-foreground-600 hover:bg-background-100 cursor-pointer"><i className="ri-upload-2-line" />上傳</button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <select value={item.paymentStatus} onChange={(e) => handlePaymentStatusChange(item.id, e.target.value as PaymentStatus)} className={`text-xs font-medium px-2 py-1 rounded-full border-none outline-none cursor-pointer ${getPaymentStatusColor(item.paymentStatus)}`}>
                      <option value="未付款">待處理</option><option value="已通知">待處理</option><option value="已付款">已完成</option>
                    </select>
                    {user.isAdmin && <button onClick={() => handleDeleteItem(item.id)} className="ml-2 text-xs text-accent-600 hover:text-accent-800" title="刪除訂單"><i className="ri-delete-bin-line" /></button>}
                  </td>
                </tr>
              ))}
              {paginatedItems.length === 0 && !loading && (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-foreground-400">
                    <i className="ri-inbox-line text-2xl mb-2 block" />
                    找不到符合條件的帳務紀錄
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-t border-background-200">
          <p className="text-xs text-foreground-500">
            共 {filteredItems.length} 筆，第 {currentPage} / {totalPages} 頁
          </p>
          <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-2 py-1 rounded-md text-xs text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <i className="ri-arrow-left-s-line" /> <span className="hidden sm:inline">Previous</span>
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let page: number;
              if (totalPages <= 5) {
                page = i + 1;
              } else if (currentPage <= 3) {
                page = i + 1;
              } else if (currentPage >= totalPages - 2) {
                page = totalPages - 4 + i;
              } else {
                page = currentPage - 2 + i;
              }
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`${currentPage === page ? "flex" : "hidden sm:flex"} w-8 h-8 items-center justify-center rounded-md text-xs font-medium cursor-pointer transition-colors ${
                    currentPage === page
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {page}
                </button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <span className="hidden sm:inline text-xs text-foreground-400 px-1">...</span>
            )}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-2 py-1 rounded-md text-xs text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <span className="hidden sm:inline">Next</span> <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
